<script lang="ts">
    import type { QueueItemMetadata } from '~/jet/models';
    import LineClamp from '@amp/web-app-components/src/components/LineClamp/LineClamp.svelte';
    import ProductContextMenu from '~/components/jet/ProductContextMenu.svelte';
    import MetadataFavoriteButton from '~/components/structure/Players/components/MetadataFavoriteButton.svelte';
    import { getI18n } from '~/stores/i18n';
    import { getRafQueue } from '@amp/web-app-components/src/utils/rafQueue';
    import { getCommerce } from '~/utils/commerce';
    import { isPremiumSubscriber } from '~/utils/user';

    export let item: QueueItemMetadata | null = null;
    export let hasMultiLineTitle: boolean = true;
    export let showAccessoryButtons: boolean = true;
    export let isMiniPlayerMeta: boolean = false;

    // Pass-through property to indicate where to render <amp-contextual-menu />
    // otherwise it defaults to document.body
    export let parentElement: HTMLElement = null;

    const i18n = getI18n();
    const rafQueue = getRafQueue();
    const { state: commerceState } = getCommerce();

    let multilineTitle = true; // Default to 3 lines title and 1 line subtitle
    let multilineSubtitle = false; // Used for conditional focus styling on subtitle

    $: artistName = item?.artistName;
    $: albumName = item?.albumName;
    $: composerName = item?.composerName;
    $: movementName = item?.movementName;
    $: workName = item?.workName;

    $: isPremiumUser = isPremiumSubscriber(
        $commerceState?.user?.subscriberType,
    );

    $: primaryLineContent = (() => {
        if (!item) {
            return '';
        }

        if (composerName && workName) {
            return $i18n.t('ASE.Web.MusicClassical.ContentA.Colon.ContentB', {
                contentA: composerName,
                contentB: workName,
            });
        }

        return composerName ?? workName;
    })();

    $: secondaryLineContent = (() => {
        if (!item) {
            return '';
        }

        if (movementName && artistName) {
            return $i18n.t('ASE.Web.MusicClassical.ContentA.EmDash.ContentB', {
                contentA: movementName,
                contentB: artistName,
            });
        }

        return movementName ?? artistName;
    })();

    // Schedule multiline updates with RAF queue
    $: updateMultilineTitle = (e: CustomEvent) =>
        rafQueue.add(() => (multilineTitle = e.detail.multiline));

    $: updateMultilineSubtitle = (e: CustomEvent) =>
        rafQueue.add(() => (multilineSubtitle = e.detail.multiline));
</script>

<div
    class="metadata"
    class:metadata--mini-player={isMiniPlayerMeta}
    data-testid="now-playing-metadata"
>
    {#if item}
        <div
            class="metadata__song-details-content"
            data-testid="metadata-content-container"
            dir="auto"
        >
            <div
                class="metadata__title-container"
                class:metadata--multiline={multilineTitle}
            >
                <LineClamp
                    clamp={hasMultiLineTitle ? 3 : 1}
                    allowFocusOverflow={true}
                    on:resize={updateMultilineTitle}
                    observe={true}
                >
                    <span data-testid="metadata-title" class="metadata__title">
                        {primaryLineContent}
                    </span>
                </LineClamp>
            </div>

            {#if secondaryLineContent}
                <div
                    data-testid="metadata-subtitles"
                    class="metadata__subtitle-container"
                >
                    <LineClamp
                        clamp={1}
                        allowFocusOverflow={true}
                        on:resize={updateMultilineSubtitle}
                        observe={true}
                    >
                        <span
                            data-testid="metadata-subtitle"
                            class="metadata__subtitle"
                            >{secondaryLineContent}
                        </span>
                    </LineClamp>
                </div>
            {/if}
        </div>

        {#if showAccessoryButtons}
            <div
                class="accessory-buttons"
                data-testid="accessory-buttons-container"
            >
                {#if isPremiumUser && item}
                    <MetadataFavoriteButton id={item.id} />
                {/if}

                {#if item?.contextMenuAction}
                    <ProductContextMenu
                        contextMenuAction={item.contextMenuAction}
                        hasMaterial={false}
                        bind:parentElement
                    />
                {/if}
            </div>
        {/if}
    {/if}
</div>

<style lang="scss">
    .metadata {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .metadata:not(.metadata--mini-player) {
        --lcd-primary-text-color: var(--systemPrimary-onDark);
        --lcd-secondary-text-color: var(--systemSecondary-onDark);
        column-gap: 18px;

        @media (--small) {
            padding-top: 22px;
        }

        .metadata__subtitle-container {
            margin-top: 2px;
        }
    }

    .metadata__title-container {
        width: 100%;
        display: grid;
        color: var(--lcd-primary-text-color, var(--systemPrimary));
    }

    .metadata__title {
        font: var(--body-emphasized-serif);
    }

    .metadata__subtitle-container {
        color: var(--lcd-secondary-text-color, var(--systemSecondary));
    }

    .metadata__subtitle {
        font: var(--callout);
    }

    .metadata--mini-player {
        .metadata__title,
        .metadata__subtitle {
            font: var(--callout);
        }
    }

    .accessory-buttons {
        // Context menu button overrides
        --contextMenuCircleFillOverride: var(--systemQuaternary-onDark);
        --contextMenuCircleHoverFill: var(--systemQuaternary-onDark);

        // Favorite button overrides
        --favoriteButtonSize: 28px;
        --favoriteButtonBackground: var(--systemQuaternary-onDark);
        --favoriteButtonStarOutline: var(--systemPrimary-onDark);
        --favoriteButtonStarCutout: var(--systemSecondary-onDark);
        --favoriteButtonStarFill: transparent;

        // Reset Favorite button hover styles to match non-hover colors
        --favoriteButtonBackground-hover: var(--favoriteButtonBackground);
        --favoriteButtonStarOutline-hover: var(--favoriteButtonStarOutline);
        --favoriteButtonStarCutout-hover: var(--favoriteButtonStarCutout);
        --favoriteButtonStarFill-hover: var(--favoriteButtonStarFill);
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 16px;
        padding-top: 22px;
        padding-inline-start: 4px;
    }
</style>
